import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { EASE } from "@/lib/site";

const CHAPTERS = [
  {
    number: "01",
    label: "MODELLING",
    title: "Twins of the network, and every asset on it",
    description:
      "Physics-based digital twins model every part of the network topology, capturing every nuance of asset-level behavior and how each asset behaves in the integrated system.",
    path: "M0 26 C 20 26, 24 8, 40 8 S 58 40, 74 40 S 92 14, 108 14 S 126 32, 144 32",
    testid: "tech-chapter-01-modelling",
  },
  {
    number: "02",
    label: "FORECASTING",
    title: "Seeing the grid before it happens",
    description:
      "Hyperlocal weather, load, and generation forecasts feed our models constantly with data, ensuring that we don't react, but respond to an uncertain future.",
    path: "M0 40 C 30 42, 45 38, 60 26 S 90 10, 110 8 S 135 6, 144 4",
    testid: "tech-chapter-02-forecasting",
  },
  {
    number: "03",
    label: "ASSET HEALTH",
    title: "Modelling degradation, not just output",
    description:
      "Our in-house degradation models always weigh the cost of a decision against the value it generates, ensuring that you never make a loss-making decision. Every dispatch decision accounts for its effect on asset lifetime, so that short-term gains don't come at the cost of asset life.",
    path: "M0 8 C 35 10, 55 18, 80 28 S 125 40, 144 42",
    testid: "tech-chapter-03-asset-health",
  },
  {
    number: "04",
    label: "PLANNING",
    title: "Multi-day dispatch, built for profit",
    description:
      "Day-ahead, multi-day, and rolling five-minute plans are solved together, weighing revenue opportunity against asset wear, changing weather patterns and grid constraints. We constantly learn and re-think our strategies, so that even unforeseen circumstances lead to predictable outcomes.",
    path: "M0 36 L 18 36 L 18 20 L 42 20 L 42 44 L 70 44 L 70 12 L 98 12 L 98 30 L 126 30 L 126 8 L 144 8",
    testid: "tech-chapter-04-planning",
  },
  {
    number: "05",
    label: "CONTROL",
    title: "Sub-second response to honor grid code",
    description:
      "Our on-site control layer continuously monitors your hardware. Inverter and governor responses execute at the edge in real time, enforcing frequency, voltage, and ramp-rate limits as specified by the grid codes without a round trip to the cloud.",
    path: "M0 24 Q 12 4, 24 24 T 48 24 T 72 24 T 96 24 T 120 24 T 144 24",
    testid: "tech-chapter-05-control",
  },
];

export const Technology = ({ open, setOpen }) => {

  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="py-20 sm:py-28 lg:py-32 border-t border-[#E2DFD7]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#2C4A3A] font-semibold">
                02 / Technology
              </p>
              <h2 className="mt-6 text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-[#121413]">
                How the system works.
              </h2>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.24em] text-[#7A7E7A]">
                Modelling — Forecasting — Asset Health — Planning — Control
              </p>
            </div>
            <button
              data-testid="technology-expand-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="border border-[#121413] px-5 py-2.5 text-sm text-[#121413] hover:bg-[#121413] hover:text-[#F5F4F0] transition-colors duration-300"
            >
              {open ? "Collapse −" : "Expand +"}
            </button>
          </div>
        </Reveal>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-12 border-t border-[#E2DFD7]">
                {CHAPTERS.map((c) => (
                  <article
                    key={c.number}
                    data-testid={c.testid}
                    className="grid grid-cols-12 gap-6 items-center py-10 sm:py-12 border-b border-[#E2DFD7] group"
                  >
                    <p className="col-span-3 sm:col-span-2 font-mono text-4xl sm:text-5xl lg:text-6xl font-light text-[#C5C1B6] group-hover:text-[#2C4A3A] transition-colors duration-500">
                      {c.number}
                    </p>
                    <div className="col-span-9 sm:col-span-6 lg:col-span-7 transition-transform duration-500 group-hover:translate-x-2">
                      <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#B86A28]">
                        {c.label}
                      </p>
                      <h3 className="mt-3 text-xl sm:text-2xl font-medium tracking-tight text-[#121413]">
                        {c.title}
                      </h3>
                      <p className="mt-3 max-w-xl text-base leading-relaxed text-[#4A4D4A]">
                        {c.description}
                      </p>
                    </div>
                    <div className="hidden lg:flex col-span-3 justify-end">
                      <svg
                        viewBox="0 0 144 48"
                        className="w-40 h-12 text-[#2C4A3A] opacity-60 group-hover:opacity-100 transition-opacity duration-500"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d={c.path} stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                    </div>
                  </article>
                ))}
                <div className="mt-10 border-l-2 border-[#2C4A3A] bg-[#E8EFEB]/50 p-6 sm:p-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C4A3A] font-semibold">
                    Continuous learning
                  </p>
                  <p
                    data-testid="technology-closing-line"
                    className="mt-3 text-lg sm:text-xl font-medium leading-relaxed text-[#121413] max-w-3xl"
                  >
                    The system starts learning from your site's own data on
                    day one. By day thirty, it's dispatching against a model
                    that's been continuously refined on your grid's actual
                    behavior, not the one it launched with.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
